import React from 'react';
import PropTypes from 'prop-types';
import Platform from "react-native-web/dist/exports/Platform";
import Pressable from "react-native-web/dist/exports/Pressable";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import Text from "react-native-web/dist/exports/Text";
import View from "react-native-web/dist/exports/View";
import { applyTextStyles, useTheme, useThemeTokensCallback } from '../ThemeProvider';
import { a11yProps, clickProps, getTokensPropType, linkProps, resolvePressableTokens, selectSystemProps, selectTokens, variantProp, viewProps, withLinkRouter } from '../utils';
import Spacer from '../Spacer';
import { horizontalScrollUtils } from '../HorizontalScroll';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);
const {
  itemPositionsPropType,
  getItemPositionLayoutHandler
} = horizontalScrollUtils;
const selectHighlightBarStyles = _ref => {
  let {
    highlightColor,
    highlightBarBorderRadius = 0,
    highlightBarBorderWidth = 0,
    highlightBarHeight = 0
  } = _ref;
  return {
    backgroundColor: highlightColor,
    borderColor: highlightColor,
    height: highlightBarHeight + highlightBarBorderWidth * 2,
    borderRadius: highlightBarBorderRadius,
    borderWidth: highlightBarBorderWidth,
    bottom: -1 * (highlightBarHeight + highlightBarBorderWidth),
    left: 0,
    right: 0,
    zIndex: 1 + highlightBarBorderWidth
  };
};
const selectHighlightTriangleStyles = _ref2 => {
  let {
    highlightColor,
    highlightTriangleSize
  } = _ref2;
  return {
    container: {
      bottom: 0 - highlightTriangleSize * 2
    },
    triangle: {
      height: 0,
      width: 0,
      borderWidth: highlightTriangleSize,
      borderTopColor: highlightColor,
      borderBottomColor: 'transparent',
      borderLeftColor: 'transparent',
      borderRightColor: 'transparent'
    }
  };
};
const selectContainerStyles = _ref3 => {
  let {
    backgroundColor,
    borderColor,
    borderWidth = 0,
    borderRadius,
    paddingHorizontal = 0,
    paddingVertical = 0,
    marginHorizontal = 0,
    marginVertical = 0
  } = _ref3;
  return {
    backgroundColor,
    borderColor,
    borderWidth,
    borderRadius,
    paddingHorizontal: paddingHorizontal - borderWidth,
    paddingVertical: paddingVertical - borderWidth,
    marginLeft: marginHorizontal,
    marginRight: marginHorizontal,
    marginTop: marginVertical,
    marginBottom: marginVertical
  };
};

/**
 * Item within a Tabs component representing one tab of content. May be rendered as a link
 * or as a "menuitem" button depending on whether 'href' is passed.
 *
 * This is rendered automatically by `Tabs` and isn't intended be used directly.
 */
const TabsItem = /*#__PURE__*/React.forwardRef((_ref4, ref) => {
  let {
    href,
    variant,
    tokens,
    selected,
    itemPositions,
    index,
    children,
    accessibilityRole,
    accessibilityCurrent = Platform.OS === 'web' && accessibilityRole === 'link' && selected ? 'page' : undefined,
    accessibilityState = accessibilityRole === 'tab' ? {
      selected
    } : undefined,
    id,
    render,
    ...rawRest
  } = _ref4;
  // Convert onClick etc to onPress etc if used in an integration
  const {
    onPress,
    ...rest
  } = clickProps.toPressProps(rawRest);
  const {
    themeOptions
  } = useTheme();
  const getTokens = useThemeTokensCallback('TabsItem', tokens, variant);
  const resolveTokens = pressableState => resolvePressableTokens(getTokens, pressableState, {
    selected
  });
  const getPressableStyle = pressableState => {
    const {
      maxWidth
    } = resolveTokens(pressableState);
    return [{
      maxWidth
    }, Platform.OS === 'web' && {
      outline: 'none'
    }];
  };
  const handleLayout = getItemPositionLayoutHandler(itemPositions.positions, index);

  // On press, update the selection, call any press handler, and open any href in platform-appropriate way
  const openHref = href && linkProps.handleHref({
    href
  });
  const handlePress = onPress || openHref ? function () {
    if (onPress) onPress(...arguments);
    if (openHref) openHref(...arguments);
  } : undefined;
  const selectedProps = selectProps({
    accessibilityRole,
    accessibilityCurrent,
    accessibilityState,
    ...rest
  });
  React.useEffect(() => {
    // If this is selected while off-screen, scroll it into view
    if (selected) {
      const position = itemPositions.positions[index];
      const scrollEnd = itemPositions.scrollOffset + itemPositions.containerWidth;
      if (
      // is off the right edge, or
      scrollEnd && position?.end > scrollEnd ||
      // is off the left edge
      typeof position?.start === 'number' && position.start < itemPositions.scrollOffset) {
        itemPositions.scrollTo(position.start);
      }
    }
    // itemPositions is a ref object so this should only re-run when `selected` (or `index`) change
  }, [selected, index, itemPositions]);
  if (render) return render({
    selected,
    handlePress
  });
  return /*#__PURE__*/_jsx(Pressable, {
    ref: ref,
    onPress: handlePress,
    href: href,
    style: getPressableStyle,
    onLayout: handleLayout,
    nativeID: id,
    ...selectedProps,
    children: pressableState => {
      const {
        space,
        textAlign,
        ...themeTokens
      } = resolveTokens(pressableState);
      const hasHighlightBar = Boolean(themeTokens.highlightBarHeight);
      const highlightBarStyle = hasHighlightBar && selectHighlightBarStyles(themeTokens);
      const hasHighlightTriangle = Boolean(themeTokens.highlightTriangleSize);
      const highlightTriangleStyle = hasHighlightTriangle && selectHighlightTriangleStyles(themeTokens);
      const containerStyles = selectContainerStyles(themeTokens);
      const textStyles = applyTextStyles({
        ...selectTokens('Typography', themeTokens),
        themeOptions,
        textAlign
      });
      return /*#__PURE__*/_jsxs(View, {
        style: staticStyles.container,
        children: [/*#__PURE__*/_jsx(View, {
          style: containerStyles,
          children: /*#__PURE__*/_jsx(Text, {
            style: textStyles,
            children: children
          })
        }), /*#__PURE__*/_jsx(Spacer, {
          space: space
        }), hasHighlightBar && /*#__PURE__*/_jsx(View, {
          style: [staticStyles.absolute, highlightBarStyle]
        }), hasHighlightTriangle && /*#__PURE__*/_jsx(View, {
          style: [staticStyles.absolute, highlightTriangleStyle.container],
          children: /*#__PURE__*/_jsx(View, {
            style: highlightTriangleStyle.triangle
          })
        })]
      });
    }
  });
});
TabsItem.displayName = 'TabsItem';
TabsItem.propTypes = {
  ...selectedSystemPropTypes,
  tokens: getTokensPropType('TabsItem'),
  variant: variantProp.propType,
  onPress: PropTypes.func,
  href: PropTypes.string,
  index: PropTypes.number,
  selected: PropTypes.bool,
  itemPositions: itemPositionsPropType,
  children: PropTypes.string,
  id: PropTypes.string,
  render: PropTypes.func
};
const staticStyles = StyleSheet.create({
  center: {
    alignItems: 'center'
  },
  absolute: {
    position: 'absolute',
    alignItems: 'center',
    left: 0,
    right: 0
  },
  container: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        pointerEvents: 'none'
      }
    })
  }
});
export default withLinkRouter(TabsItem);